import { createAction, createEntityAdapter, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { KeycloakLoginOptions } from 'keycloak-js'

import auth, { AuthState, AuthUser, LoginRequest, OnlineStaff, UpdateAuthUserDto } from '@/core/classes/Auth'

import { RootState } from '@/redux/rootReducer'
import { PluginTest } from '@/core/models/staff.model'
import { SubscriptionClass } from '@/utils/subscription/subscription'
import { SubscriptionType } from '@/utils/subscription/types'
import { Event } from '@/core/models/notification.model'
import { adminApi } from '@/core/api/admin'
import { Slot } from '@/core/models/schedule.model'
import { waitingRoomApi } from '@/core/api/waiting-room'
import { isStaff } from '@/utils/type-guards'
import { authApi } from '@/core/api/auth'

export type AuthReducerState = AuthState

export const selectAuthUser = (state: RootState): AuthUser | null => state.auth.authUser
export const selectAccessToken = (state: RootState): string | undefined => state.auth.tokens.access || undefined
export const selectCalendsoPassword = (state: RootState) => state.auth.calendsoPassword
export const selectIsAuthenticated = (state: RootState): boolean => state.auth.isAuthenticated
export const selectIsOfflineMode = (state: RootState): boolean => state.auth.isOfflineMode || false
export const selectOnlineStaffs = (state: RootState) => state.auth.onlineStaffs
export const selectOnlineUsers = (state: RootState) => state.auth.onlineUsers
export const selectMagicLink = (state: RootState) => state.auth.magicLink

export const selectAuthGetRequestTracker = (state: RootState) => state.auth.requestTracker.get
export const selectAuthCreateRequestTracker = (state: RootState) => state.auth.requestTracker.create
export const selectAuthUserUpdateRequestState = (state: RootState) => state.auth.requestTracker.update
export const selectAuthLoginRequestTracker = (state: RootState) => state.auth.requestTracker.login
export const selectStartTwilioVerifyRequestTracker = (state: RootState) => state.auth.requestTracker.startTwilioVerify
export const selectCheckTwilioVerifyRequestTracker = (state: RootState) => state.auth.requestTracker.checkTwilioVerify
export const selectAuthEmail = (state: RootState): string | undefined => state.auth.authUser?.email || undefined
export const selectAuthUserSubscription = (state: RootState): SubscriptionClass =>
  new SubscriptionClass(state.auth.subscription.type, state.auth.subscription.subscription, state.auth.subscription.layout)
export const selectMagicLinkGetRequestTracker = (state: RootState) => state.auth.requestTracker.getMagicLink

export const EventsAdapter = createEntityAdapter<Event, Event['_id']>({
  selectId: (event) => event._id,
})

const eventsSelector = EventsAdapter.getSelectors((state: RootState) => state.auth.events)

export const selectEvents = eventsSelector.selectAll
export const selectEventById = eventsSelector.selectById

const initialState: AuthReducerState = {
  ...auth.getInitialState(),
  requestTracker: {
    ...auth.getInitialState().requestTracker,
    getCalendsoPassword: { error: null, loading: false },
    startTwilioVerify: { error: null, loading: false },
    checkTwilioVerify: { error: null, loading: false },
    getMagicLink: { error: null, loading: false },
  },
}

const reducerName = 'auth'

const authSlice = createSlice({
  name: reducerName,
  initialState,
  reducers: {
    getAccountLink(state, action: PayloadAction<{ refresh_url?: string; return_url?: string } | undefined>) {
      state.requestTracker.get.loading = true
    },
    getAccountLinkSuccess(state, action: PayloadAction<string>) {
      if (state.authUser) state.authUser.accountLinkUrl = action.payload
      state.requestTracker.get.error = null
      state.requestTracker.get.loading = false
    },
    getAccountLinkFailure(state, action) {
      state.requestTracker.get.error = action.payload
      state.requestTracker.get.loading = false
    },
    getCalendsoPassword(state) {
      state.requestTracker.getCalendsoPassword.loading = true
    },
    getCalendsoPasswordSuccess(state, action: PayloadAction<string>) {
      state.calendsoPassword = action.payload
      state.requestTracker.getCalendsoPassword.error = null
      state.requestTracker.getCalendsoPassword.loading = false
    },
    getCalendsoPasswordFailure(state, action) {
      state.requestTracker.getCalendsoPassword.error = action.payload
      state.requestTracker.getCalendsoPassword.loading = false
    },
    keycloakAuthSuccess() {},
    login(state, action: PayloadAction<KeycloakLoginOptions>) {},
    logout() {},
    loginSilent: {
      reducer: (state, action: PayloadAction<LoginRequest>) => {
        state.requestTracker.login.loading = true
      },
      prepare: (login: LoginRequest) => ({
        payload: {
          ...login,
        },
      }),
    },
    loginSilentSuccess(state) {
      state.requestTracker.login.loading = false
      state.isAuthenticated = true
    },
    loginSilentFailure(state, action) {
      state.requestTracker.login.loading = false
      state.requestTracker.login.error = action.payload
    },
    modifyPassword(state, action: PayloadAction<string>) {
      state.requestTracker.update.loading = true
    },
    modifyPasswordSuccess(state) {
      state.requestTracker.update.error = null
      state.requestTracker.update.loading = false
    },
    modifyPasswordFailure(state, action) {
      state.requestTracker.update.error = action.payload
      state.requestTracker.update.loading = false
    },
    refreshToken() {},
    setAccessToken(state, action: PayloadAction<string | null>) {
      state.tokens.access = action.payload
    },
    setAuthUser(state, action) {
      state.authUser = action.payload
      state.subscription = {
        type: state.authUser?.subscription as SubscriptionType,
      }
    },
    setAuthState(state, action: PayloadAction<boolean>) {
      state.isAuthenticated = action.payload
    },
    setRefreshToken(state, action: PayloadAction<string | null>) {
      state.tokens.refresh = action.payload
    },
    updateMyProfile(state, action: PayloadAction<UpdateAuthUserDto>) {
      state.requestTracker.update.loading = true
    },
    updateMyProfileSuccess(state, action: PayloadAction<AuthUser>) {
      state.authUser = action.payload
      state.requestTracker.update.error = null
      state.requestTracker.update.loading = false
    },
    updateMyProfileFailure(state, action) {
      state.requestTracker.update.error = action.payload
      state.requestTracker.update.loading = false
    },
    startTwilioVerify(state, action: PayloadAction<{ to: string; channel: 'email' | 'sms' }>) {
      state.requestTracker.startTwilioVerify.loading = true
    },
    startTwilioVerifySuccess(state) {
      state.requestTracker.startTwilioVerify.error = null
      state.requestTracker.startTwilioVerify.loading = false
    },
    startTwilioVerifyFailure(state, action) {
      state.requestTracker.startTwilioVerify.error = action.payload
      state.requestTracker.startTwilioVerify.loading = false
    },
    checkTwilioVerify(state, action: PayloadAction<{ to: string; code: string }>) {
      state.requestTracker.checkTwilioVerify.loading = true
    },
    checkTwilioVerifySuccess(state) {
      state.requestTracker.checkTwilioVerify.error = null
      state.requestTracker.checkTwilioVerify.loading = false
    },
    checkTwilioVerifyFailure(state, action) {
      state.requestTracker.checkTwilioVerify.error = action.payload
      state.requestTracker.checkTwilioVerify.loading = false
    },
    testPlugin(state) {},
    testPluginSuccess(state, action: PayloadAction<[PluginTest]>) {
      // if(state.authUser) state.authUser.plugins = action.payload
    },
    testPluginFailure(state, action) {
      state.requestTracker.create.error = action.payload
    },
    onlineList(state, action: PayloadAction<OnlineStaff[]>) {
      const authUser = state.authUser
      state.onlineStaffs = action.payload.filter((staff) => staff.staffId !== authUser?._id)
    },
    onlineUsers(state, action: PayloadAction<string[]>) {
      const authUser = state.authUser
      state.onlineUsers = action.payload.filter((id) => id !== authUser?._id)
    },
    getCalcomInfos(state) {},
    getCalcomInfosSuccess(state, action: PayloadAction<{ link: string; username: string; password: string }>) {
      state.calcom = action.payload
    },
    getCalcomInfosFailure(state, action) {
      state.requestTracker.get.error = action.payload
    },
    getMyAvailability(state) {
      state.requestTracker.get.error = null
    },
    getMyAvailabilitySuccess(state, action: PayloadAction<Slot[]>) {
      state.availability = action.payload
      state.requestTracker.get.error = null
    },
    getMyAvailabilityFailure(state, action) {
      state.requestTracker.get.error = action.payload
    },
    updateMyAvailability(state, action: PayloadAction<Pick<Slot, 'start' | 'end'>[]>) {
      state.requestTracker.update.loading = true
    },
    updateMyAvailabilitySuccess(state, action: PayloadAction<Pick<Slot, 'start' | 'end'>[]>) {
      state.availability = action.payload
      state.requestTracker.update.error = null
      state.requestTracker.update.loading = false
    },
    updateMyAvailabilityFailure(state, action) {
      state.requestTracker.update.error = action.payload
      state.requestTracker.update.loading = false
    },

    getMyNotifications(state, action: PayloadAction<{ limit: number; skip: number }>) {
      state.requestTracker.get.error = null
    },
    getMyNotificationsSuccess(state, action: PayloadAction<[Event[], number]>) {
      EventsAdapter.upsertMany(state.events, action.payload[0])
      state.totalEvents = action.payload[1]
      state.requestTracker.get.error = null
    },
    getMyNotificationsFailure(state, action) {
      state.requestTracker.get.error = action.payload
    },
    markNotificationAsRead(state, action: PayloadAction<{ eventId: string }>) {
      state.requestTracker.update.loading = true
    },
    markNotificationAsReadSuccess(state, action: PayloadAction<Event>) {
      EventsAdapter.upsertOne(state.events, action.payload)
      state.requestTracker.update.error = null
      state.requestTracker.update.loading = false
    },
    markNotificationAsReadFailure(state, action) {
      state.requestTracker.update.error = action.payload
      state.requestTracker.update.loading = false
    },
    getMagicLink(state, action: PayloadAction<string>) {
      state.requestTracker.getMagicLink.loading = true
    },
    getMagicLinkSuccess(state, action: PayloadAction<string>) {
      state.magicLink = action.payload
      state.requestTracker.getMagicLink.error = null
      state.requestTracker.getMagicLink.loading = false
    },
    getMagicLinkFailure(state, action) {
      state.requestTracker.getMagicLink.error = action.payload
      state.requestTracker.getMagicLink.loading = false
    },
    checkStripeAccount(state, action: PayloadAction<{ staffId: string }>) {
      state.requestTracker.get.loading = true
    },
    checkStripeAccountSuccess(state, action: PayloadAction<{ staff: AuthUser }>) {
      state.requestTracker.get.error = null
      state.requestTracker.get.loading = false
      state.authUser = action.payload.staff
    },
    checkStripeAccountFailure(state, action) {
      state.requestTracker.get.error = action.payload
      state.requestTracker.get.loading = false
    },
    setOfflineAuth(state, action: PayloadAction<{ user: any; token: any; isOffline: boolean }>) {
      const { user, token, isOffline } = action.payload
      state.authUser = user
      state.tokens.access = token
      state.isAuthenticated = true
      state.isOfflineMode = isOffline
    },
    setOnlineMode(state) {
      state.isOfflineMode = false
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(authApi.endpoints.updateMyProfile.matchFulfilled, (state, action) => {
        state.authUser = action.payload
      })
      .addMatcher(adminApi.endpoints.updateEstablismentLogo.matchFulfilled, (state, action) => {
        if (state.authUser?.establishment) state.authUser.establishment.logo = action.payload.logo
      })
      .addMatcher(waitingRoomApi.endpoints.createWaitingRoom.matchFulfilled, (state, action) => {
        if (isStaff(state.authUser)) state.authUser.activeWaitingRooms.push(action.payload._id)
      })
      .addMatcher(waitingRoomApi.endpoints.joinWaitingRoom.matchFulfilled, (state, action) => {
        if (isStaff(state.authUser)) state.authUser.activeWaitingRooms.push(action.payload._id)
      })
      .addMatcher(waitingRoomApi.endpoints.leaveWaitingRoom.matchFulfilled, (state, action) => {
        if (isStaff(state.authUser)) {
          state.authUser.activeWaitingRooms = state.authUser.activeWaitingRooms.filter((waitingRoom) => waitingRoom !== action.payload._id)
        }
      })
  },
})

export const online = createAction<string>(`${reducerName}/online`)

export const {
  getAccountLink,
  getAccountLinkSuccess,
  getAccountLinkFailure,
  getCalendsoPassword,
  getCalendsoPasswordSuccess,
  getCalendsoPasswordFailure,
  keycloakAuthSuccess,
  login,
  logout,
  modifyPassword,
  modifyPasswordSuccess,
  modifyPasswordFailure,
  refreshToken,
  setAccessToken,
  setAuthUser,
  setAuthState,
  setRefreshToken,
  updateMyProfile,
  updateMyProfileSuccess,
  updateMyProfileFailure,
  startTwilioVerify,
  startTwilioVerifySuccess,
  startTwilioVerifyFailure,
  checkTwilioVerify,
  checkTwilioVerifySuccess,
  checkTwilioVerifyFailure,
  loginSilent,
  loginSilentSuccess,
  loginSilentFailure,
  testPlugin,
  testPluginSuccess,
  testPluginFailure,
  onlineList,
  onlineUsers,
  getCalcomInfos,
  getCalcomInfosSuccess,
  getCalcomInfosFailure,
  getMyAvailability,
  getMyAvailabilitySuccess,
  getMyAvailabilityFailure,
  updateMyAvailability,
  updateMyAvailabilitySuccess,
  updateMyAvailabilityFailure,
  getMyNotifications,
  getMyNotificationsSuccess,
  getMyNotificationsFailure,
  markNotificationAsRead,
  markNotificationAsReadSuccess,
  markNotificationAsReadFailure,
  getMagicLink,
  getMagicLinkSuccess,
  getMagicLinkFailure,
  checkStripeAccount,
  checkStripeAccountSuccess,
  checkStripeAccountFailure,
  setOfflineAuth,
  setOnlineMode,
} = authSlice.actions

export default authSlice.reducer
